import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { filter, take, timeout } from 'rxjs/operators';
import { NGXLogger } from 'ngx-logger';
import { AppSettingsService } from './AppSettings.service';

declare const grecaptcha: any;

/** Koliko se najduže čeka `app/public` pre nego što se odustane od reCAPTCHA. */
const SETTINGS_TIMEOUT_MS = 5000;

/** Koliko se najduže čeka token od grecaptcha. */
const EXECUTE_TIMEOUT_MS = 10000;

/**
 * Google reCAPTCHA v3 za javne forme (registracija naloga).
 *
 * Site key stiže sa servera (`app/public` → `recaptchaSiteKey`), pa isti build radi na
 * svim domenima i ključ se menja bez rebuild-a. Skripta se učitava LENJO — tek kad se
 * forma koja je traži otvori; aplikacije koje registraciju ne prikazuju (kiosk, konobar,
 * KD…) nikad ne povuku ništa sa Google-a.
 *
 * `execute()` nikad ne baca: bez ključa ili bez interneta vraća null i forma nastavlja —
 * odluku donosi server (bez podešenog tajnog ključa provera je isključena, sa ključem
 * prazan token znači odbijanje).
 */
@Injectable({
  providedIn: 'root'
})
export class RecaptchaService {
  private scriptPromise?: Promise<boolean>;
  private siteKey?: string;

  constructor(private appSettings: AppSettingsService, private logger: NGXLogger) { }

  /** True kad je reCAPTCHA konfigurisana na serveru — forma po tome zna da li da čeka token. */
  async isEnabled(): Promise<boolean> {
    return !!(await this.getSiteKey());
  }

  /**
   * Vraća token za zadatu akciju (npr. "register"), ili null ako reCAPTCHA nije
   * konfigurisana / skripta nije mogla da se učita.
   */
  async execute(action: string): Promise<string | null> {
    const siteKey = await this.getSiteKey();
    if (!siteKey) {
      return null;
    }

    const loaded = await this.load(siteKey);
    if (!loaded) {
      return null;
    }

    try {
      const token = new Promise<string>((resolve, reject) => {
        grecaptcha.ready(() => {
          grecaptcha.execute(siteKey, { action }).then(resolve, reject);
        });
      });

      // Ni u jednom slučaju forma ne sme da ostane zaglavljena na čekanju tokena.
      const guard = new Promise<null>(resolve => setTimeout(() => resolve(null), EXECUTE_TIMEOUT_MS));

      return await Promise.race([token, guard]);
    } catch (e) {
      this.logger.warn('reCAPTCHA execute failed', e);

      return null;
    }
  }

  private async getSiteKey(): Promise<string | undefined> {
    if (this.siteKey !== undefined) {
      return this.siteKey || undefined;
    }

    try {
      // Timeout je obavezan: ako `app/public` ne odgovori, BehaviorSubject nikad ne emituje
      // popunjene postavke i forma bi visila na await-u umesto da posalje zahtev.
      const settings = await firstValueFrom(
        this.appSettings.current.pipe(
          filter(s => !!s && Object.keys(s).length > 0),
          take(1),
          timeout(SETTINGS_TIMEOUT_MS)
        )
      );

      this.siteKey = settings.recaptchaSiteKey || '';
    } catch {
      this.logger.warn('reCAPTCHA: nije stigao site key sa servera');
      this.siteKey = '';
    }

    return this.siteKey || undefined;
  }

  private load(siteKey: string): Promise<boolean> {
    if (!this.scriptPromise) {
      this.scriptPromise = new Promise<boolean>(resolve => {
        const script = document.createElement('script');
        script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve(true);
        script.onerror = () => {
          this.logger.warn('reCAPTCHA script could not be loaded');
          resolve(false);
        };

        document.head.appendChild(script);
      });
    }

    return this.scriptPromise;
  }
}
